import { createFileRoute, redirect } from '@tanstack/react-router'
import { CircleAlert, RefreshCw } from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  useRewardPricingPublish,
  useRewardPricingRetrieve,
  useV1MeRetrieve,
} from '@/api/client'
import type { PricingError } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import {
  RewardPricingPage,
  type PricingPublishResult,
} from '@/components/reward-pricing/reward-pricing-page'
import type { PricingWriteItem } from '@/components/reward-pricing/reward-pricing-form'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

export const Route = createFileRoute('/_panel/punktacja')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: RewardPricingRoute,
})

function RewardPricingRoute() {
  const meQuery = useV1MeRetrieve()
  const pricingQuery = useRewardPricingRetrieve({
    query: { refetchOnWindowFocus: false, refetchOnReconnect: false },
  })
  const mutation = useRewardPricingPublish()

  const canEdit =
    meQuery.data?.status === 200 &&
    (meQuery.data.data.role === 'admin' || meQuery.data.data.role === 'superadmin')
  const pricing = pricingQuery.data?.status === 200 ? pricingQuery.data.data : undefined
  const failed =
    pricingQuery.isError || (pricingQuery.data !== undefined && pricingQuery.data.status !== 200)

  const refresh = async () => {
    await pricingQuery.refetch()
  }

  const publish = async (
    expectedVersion: number,
    items: PricingWriteItem[],
  ): Promise<PricingPublishResult> => {
    try {
      const response = await mutation.mutateAsync({
        data: { expected_version: expectedVersion, items },
      })
      if (response.status === 409) return { status: 'conflict' }
      if (response.status !== 200) {
        const parsed = parsePricingError(response.data, response.status)
        return { status: 'error', message: parsed.message, itemErrors: parsed.itemErrors }
      }
      await pricingQuery.refetch()
      return { status: 'success' }
    } catch {
      return {
        status: 'error',
        message:
          'Nie udało się potwierdzić wyniku publikacji. Pobierz aktualną wersję przed ponowieniem.',
      }
    }
  }

  return (
    <>
      <PageHeader title="Punktacja i nagrody" />
      {failed ? (
        <div className="p-6">
          <Alert variant="destructive">
            <CircleAlert />
            <AlertTitle>Nie udało się pobrać cennika</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-3">
              <p>Sprawdź połączenie i spróbuj ponownie.</p>
              <Button variant="outline" size="sm" onClick={() => void refresh()}>
                <RefreshCw data-icon="inline-start" /> Spróbuj ponownie
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      ) : !pricing ? (
        <div className="flex flex-col gap-4 p-6">
          <Skeleton className="h-36 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : (
        <RewardPricingPage
          pricing={pricing}
          canEdit={canEdit}
          publishing={mutation.isPending}
          onPublish={publish}
          onRefresh={refresh}
        />
      )}
    </>
  )
}

export function parsePricingError(
  data: unknown,
  status: number,
): { message: string; itemErrors?: Record<string, string> } {
  const fallback = 'Nie udało się opublikować cennika. Sprawdź wartości i spróbuj ponownie.'
  if (status >= 500) return { message: 'Wystąpił błąd serwera. Spróbuj ponownie później.' }

  const error = data as PricingError
  const itemErrors = itemFieldErrors(error?.items)
  if (itemErrors) {
    return { message: 'Popraw wskazane pozycje i spróbuj ponownie.', itemErrors }
  }
  return { message: firstMessage(error?.items) ?? error?.detail ?? fallback }
}

function itemFieldErrors(items: unknown): Record<string, string> | undefined {
  if (!items || typeof items !== 'object' || Array.isArray(items)) return undefined
  const entries = Object.entries(items as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string',
  )
  return entries.length > 0 ? Object.fromEntries(entries) : undefined
}

function firstMessage(value: unknown): string | null {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstMessage(item)
      if (message) return message
    }
  }
  if (value && typeof value === 'object') {
    for (const item of Object.values(value)) {
      const message = firstMessage(item)
      if (message) return message
    }
  }
  return null
}
